/**
 * The floating pill that appears once several rows are selected.
 *
 * It is the single home for select-all — putting it here rather than in the
 * column header keeps one consistent place regardless of list or card view.
 *
 * The action set depends on WHERE you are: rows in the trash can only be
 * restored or purged, everything else moves, copies or is deleted. That is the
 * lifecycle showing through, so the branch is explicit rather than a pile of
 * per-button conditions.
 *
 * There is deliberately no standalone Archive button: signed E-IMZO documents
 * cannot be deleted, so the delete confirmation archives those and deletes the
 * rest. One destructive action, one dialog that explains what it will do.
 *
 * Below `md` the labels drop and the buttons go icon-only, so the pill still
 * fits a phone viewport.
 */

import { ArchiveRestore, Check, Copy, Folder, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';

const Label = ({ children }) => <span className="hidden md:inline">{children}</span>;

export function BulkActionBar({
  count,
  allSelected,
  onToggleSelectAll,
  inTrash,
  onRestore,
  restoreLoading,
  onPurge,
  purgeLoading,
  onMove,
  onCopy,
  copyDisabled,
  onDelete,
  deleteLoading,
  onClear,
}) {
  const { t } = useTranslation(['my-docs']);

  return (
    <div className="fixed bottom-6 max-md:bottom-[calc(0.75rem+env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 max-md:gap-1 rounded-full border bg-background/95 px-3 py-2 max-md:px-2 shadow-lg backdrop-blur max-w-[calc(100vw-1rem)]">
      <span className="px-2 max-md:px-1 text-sm font-medium whitespace-nowrap">
        {t('my-docs:bulk.selectedCount', { count }) || `${count} selected`}
      </span>

      <Button variant="ghost" size="sm" className="gap-1.5" onClick={onToggleSelectAll}>
        <Check className="h-4 w-4" />
        <Label>
          {allSelected
            ? t('my-docs:bulk.deselectAll') || 'Deselect all'
            : t('my-docs:bulk.selectAll') || 'Select all'}
        </Label>
      </Button>

      <span className="w-px h-5 bg-border" />

      {inTrash ? (
        <>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-green-700 hover:text-green-700"
            onClick={onRestore}
            disabled={restoreLoading}
          >
            <ArchiveRestore className="h-4 w-4" />
            <Label>{t('my-docs:trash.restore') || 'Restore'}</Label>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-red-600 hover:text-red-600"
            onClick={onPurge}
            disabled={purgeLoading}
          >
            <Trash2 className="h-4 w-4" />
            <Label>{t('my-docs:trash.deleteForever') || 'Delete permanently'}</Label>
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={onMove}>
            <Folder className="h-4 w-4" />
            <Label>{t('my-docs:actions.move') || 'Move'}</Label>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5"
            onClick={onCopy}
            disabled={copyDisabled}
            title={
              copyDisabled
                ? t('my-docs:bulk.copyFoldersUnsupported') || "Folders can't be copied yet"
                : undefined
            }
          >
            <Copy className="h-4 w-4" />
            <Label>{t('my-docs:actions.copy') || 'Copy'}</Label>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-red-600 hover:text-red-600"
            onClick={onDelete}
            disabled={deleteLoading}
          >
            <Trash2 className="h-4 w-4" />
            <Label>{t('my-docs:bulk.delete') || 'Delete'}</Label>
          </Button>
        </>
      )}

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={onClear}
        aria-label={t('my-docs:bulk.clearSelection')}
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
